// Marketing attribution for the quiz.
//
// Visitors often land on another page of 1clickenergy.com (from an ad) and
// only later open the quiz page, so reading UTMs from the quiz page URL alone
// loses them. A small script in the Shopify theme (snippets/attribution.liquid)
// remembers the first and latest ad click in localStorage under STORAGE_KEY.
// The quiz reads that, and also captures from its own URL in case the theme
// script is not installed.

export const STORAGE_KEY = "1click_attribution";
const MAX_AGE_DAYS = 90;

// Standard UTMs, the legacy custom names our ads already use, and click IDs.
export const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "campaign_name",
  "adgroup_name",
  "keyword",
  "gclid",
  "fbclid",
  "msclkid",
];

export const touchFromUrl = (href, referrer = "", now = new Date()) => {
  let url;
  try {
    url = new URL(href);
  } catch (e) {
    return null;
  }

  const touch = {};
  TRACKED_PARAMS.forEach((param) => {
    const value = url.searchParams.get(param);
    if (value) touch[param] = value;
  });

  if (!Object.keys(touch).length) return null;

  touch.landing_page = `${url.origin}${url.pathname}`;
  if (referrer) touch.referrer = referrer;
  touch.captured_at = now.toISOString();
  return touch;
};

const isFresh = (touch, now) => {
  if (!touch || !touch.captured_at) return false;
  const age = now.getTime() - new Date(touch.captured_at).getTime();
  return age >= 0 && age <= MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
};

export const readStored = (storage) => {
  try {
    const raw = storage && storage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) || {} : {};
  } catch (e) {
    return {};
  }
};

export const writeStored = (storage, value) => {
  try {
    storage && storage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (e) {
    // storage unavailable (private mode, blocked cookies): ignore
  }
};

// Merge a new touch into the stored first/last pair.
export const recordTouch = (stored, touch) => {
  if (!touch) return stored;
  return {
    first: stored.first || touch,
    last: touch,
  };
};

// Returns everything the quiz sends to the backend:
// - firstTouch / lastTouch: full detail, saved to the new attribution fields
// - the five legacy fields the NetSuite connector already maps
export const getAttribution = ({ href, referrer, storage, now = new Date() }) => {
  let stored = readStored(storage);
  const urlTouch = touchFromUrl(href, referrer, now);

  if (urlTouch) {
    stored = recordTouch(stored, urlTouch);
    writeStored(storage, stored);
  }

  const first = isFresh(stored.first, now) ? stored.first : null;
  const last = isFresh(stored.last, now) ? stored.last : null;
  const source = last || first || {};

  return {
    firstTouch: first,
    lastTouch: last,
    utmSource: source.utm_source || null,
    utmMedium: source.utm_medium || null,
    // Ads that send the standard utm_campaign (most Google Ads templates)
    // previously arrived with no campaign at all.
    campaignName: source.campaign_name || source.utm_campaign || null,
    adgroupName: source.adgroup_name || null,
    keyword: source.keyword || source.utm_term || null,
  };
};

export const getBrowserAttribution = () => {
  let storage = null;
  try {
    storage = window.localStorage;
  } catch (e) {
    storage = null;
  }
  return getAttribution({
    href: window.location.href,
    referrer: document.referrer,
    storage,
  });
};
